import {
  addMonths,
  differenceInCalendarDays,
  differenceInCalendarMonths,
  endOfMonth,
  format,
  parseISO,
  startOfMonth,
} from "date-fns";

import type {
  FinanceCharge,
  FinanceChargeStatus,
  FinanceContract,
  FinanceExpense,
  FinanceRecurringExpense,
} from "../../types";

/** Statuses in the order a charge goes through them */
export const FINANCE_CHARGE_STATUSES: FinanceChargeStatus[] = [
  "pending",
  "sent",
  "overdue",
  "paid",
  "cancelled",
];

/** `YYYY-MM-DD` of a local date */
export const toDateOnly = (date: Date): string => format(date, "yyyy-MM-dd");

/** `YYYY-MM-01` of the month containing `date` (a Date, a `YYYY-MM-DD` or an ISO string) */
export const toMonth = (date: Date | string): string =>
  format(
    startOfMonth(typeof date === "string" ? parseISO(date) : date),
    "yyyy-MM-dd",
  );

/**
 * Timestamp stored for a payment day picked in a date input: local noon, so the
 * day (and its month) is the same in UTC and in the user's time zone.
 */
export const toNoonTimestamp = (dateOnly: string): string => {
  const date = parseISO(dateOnly);
  date.setHours(12, 0, 0, 0);
  return date.toISOString();
};

/** `YYYY-MM-DD` of a stored timestamp, in local time (for date inputs) */
export const timestampToDateOnly = (
  timestamp: string | null | undefined,
): string | null => (timestamp ? toDateOnly(parseISO(timestamp)) : null);

/** Adds `count` months to a `YYYY-MM-01` month */
export const addToMonth = (month: string, count: number): string =>
  toMonth(addMonths(parseISO(month), count));

/** Consecutive months, oldest first, from `first` (included), `count` long */
export const monthRange = (first: string, count: number): string[] =>
  Array.from({ length: count }, (_, index) => addToMonth(first, index));

const lastDayOfMonth = (month: string): string =>
  toDateOnly(endOfMonth(parseISO(month)));

/** Due date of a month for a billing day; the database caps the day at 28 */
export const dueDateFor = (month: string, day: number): string =>
  `${month.slice(0, 8)}${String(Math.min(Math.max(day, 1), 28)).padStart(2, "0")}`;

/** Rounds to cents, so sums of decimals stay exact for display */
export const roundMoney = (value: number): number =>
  Math.round(value * 100) / 100;

/**
 * Derived status of a charge. The order matters: a cancelled charge is never
 * overdue, a paid one is never overdue, and an overdue one stays overdue even
 * when it was sent.
 */
export const getChargeStatus = (
  charge: Pick<
    FinanceCharge,
    "cancelled_at" | "paid_at" | "due_date" | "sent_at"
  >,
  today: Date,
): FinanceChargeStatus => {
  if (charge.cancelled_at) return "cancelled";
  if (charge.paid_at) return "paid";
  if (charge.due_date < toDateOnly(today)) return "overdue";
  if (charge.sent_at) return "sent";
  return "pending";
};

/** Days since the due date of an unpaid charge, 0 when not overdue */
export const getDaysOverdue = (
  charge: Pick<
    FinanceCharge,
    "cancelled_at" | "paid_at" | "due_date" | "sent_at"
  >,
  today: Date,
): number =>
  getChargeStatus(charge, today) === "overdue"
    ? differenceInCalendarDays(today, parseISO(charge.due_date))
    : 0;

/** Amount a charge brought in, `paid_amount` when it differs from the amount */
export const getReceivedAmount = (
  charge: Pick<FinanceCharge, "amount" | "paid_amount">,
): number => charge.paid_amount ?? charge.amount;

/** Whether a contract (or template) runs during any day of `month` */
const isInPeriod = (
  record: { start_date: string; end_date?: string | null },
  month: string,
): boolean =>
  record.start_date <= lastDayOfMonth(month) &&
  (!record.end_date || record.end_date >= month);

/** Whether an active contract bills its monthly fee for `month` */
export const isContractBilling = (
  contract: FinanceContract,
  month: string,
): boolean => contract.status === "active" && isInPeriod(contract, month);

/** Monthly recurring revenue of `month`: paused and cancelled contracts count 0 */
export const computeMrr = (
  contracts: FinanceContract[],
  month: string,
): number =>
  roundMoney(
    contracts
      .filter((contract) => isContractBilling(contract, month))
      .reduce((sum, contract) => sum + contract.monthly_amount, 0),
  );

/**
 * Amount of the setup installment of `month`, or null when the month has none.
 * Installments are monthly from the start month; the last one absorbs the
 * rounding so the sum matches the setup amount exactly.
 */
export const getSetupInstallment = (
  contract: FinanceContract,
  month: string,
): { number: number; amount: number } | null => {
  const installments = Math.max(contract.setup_installments, 1);
  if (contract.setup_amount <= 0 || contract.status === "cancelled") {
    return null;
  }
  const index = differenceInCalendarMonths(
    parseISO(month),
    parseISO(toMonth(contract.start_date)),
  );
  if (index < 0 || index >= installments) return null;
  const regular = roundMoney(contract.setup_amount / installments);
  const amount =
    index === installments - 1
      ? roundMoney(contract.setup_amount - regular * (installments - 1))
      : regular;
  return { number: index + 1, amount };
};

export type NewFinanceCharge = Omit<FinanceCharge, "id">;
export type NewFinanceExpense = Omit<FinanceExpense, "id">;

const chargeKey = (
  contractId: unknown,
  kind: string,
  competenceMonth: string,
): string => `${String(contractId)}|${kind}|${competenceMonth}`;

/**
 * Charges `month` should have and does not have yet: the monthly fee of every
 * billing contract and the setup installment falling in that month. Running it
 * twice creates nothing the second time (same key as the database unique
 * constraint `contract_id, kind, competence_month`).
 */
export const buildChargesForMonth = (
  contracts: FinanceContract[],
  existingCharges: Pick<
    FinanceCharge,
    "contract_id" | "kind" | "competence_month"
  >[],
  month: string,
): NewFinanceCharge[] => {
  const existing = new Set(
    existingCharges
      .filter((charge) => charge.contract_id != null)
      .map((charge) =>
        chargeKey(charge.contract_id, charge.kind, charge.competence_month),
      ),
  );
  return contracts.flatMap((contract) => {
    const dueDate = dueDateFor(month, contract.billing_day);
    const charges: NewFinanceCharge[] = [];
    const setup = getSetupInstallment(contract, month);
    if (setup && !existing.has(chargeKey(contract.id, "setup", month))) {
      charges.push({
        contract_id: contract.id,
        company_id: contract.company_id,
        kind: "setup",
        description:
          contract.setup_installments > 1
            ? `${contract.name} (${setup.number}/${contract.setup_installments})`
            : contract.name,
        amount: setup.amount,
        competence_month: month,
        due_date: dueDate,
        invoice_files: [],
        receipt_files: [],
      });
    }
    if (
      contract.monthly_amount > 0 &&
      isContractBilling(contract, month) &&
      !existing.has(chargeKey(contract.id, "recurring", month))
    ) {
      charges.push({
        contract_id: contract.id,
        company_id: contract.company_id,
        kind: "recurring",
        description: contract.name,
        amount: contract.monthly_amount,
        competence_month: month,
        due_date: dueDate,
        invoice_files: [],
        receipt_files: [],
      });
    }
    return charges;
  });
};

/** Expenses `month` should have from the active templates and does not have yet */
export const buildExpensesForMonth = (
  templates: FinanceRecurringExpense[],
  existingExpenses: Pick<
    FinanceExpense,
    "recurring_expense_id" | "competence_month"
  >[],
  month: string,
): NewFinanceExpense[] => {
  const existing = new Set(
    existingExpenses
      .filter((expense) => expense.recurring_expense_id != null)
      .map(
        (expense) =>
          `${String(expense.recurring_expense_id)}|${expense.competence_month}`,
      ),
  );
  return templates
    .filter(
      (template) =>
        template.active &&
        isInPeriod(template, month) &&
        !existing.has(`${String(template.id)}|${month}`),
    )
    .map((template) => ({
      recurring_expense_id: template.id,
      description: template.description,
      category: template.category,
      supplier: template.supplier ?? null,
      amount: template.amount,
      competence_month: month,
      due_date: dueDateFor(month, template.due_day),
      files: [],
    }));
};
